import EndpointCard from '../EndpointCard';
import ParamTable from '../ParamTable';
import CodeBlock from '../CodeBlock';

export default function CurrencyBasicSection() {
  return (
    <>
      <div id="currency" className="scroll-mt-20 mt-16 mb-8">
        <h2 className="font-serif text-bone-white text-2xl mb-2">Currency</h2>
        <div className="w-12 h-px bg-bone-white/20 mb-4" />
        <p className="font-sans text-sm text-bone-white/60 leading-relaxed">
          Full real-time and historical exchange rate data powered by OANDA. Supports 180+ world
          currencies, cryptocurrency rates, and central bank official rates. No authentication required.
        </p>
      </div>

      <EndpointCard
        id="convert"
        method="GET"
        path="/api/currency/convert"
        description="Convert an amount between any two supported currencies using real-time spot rates."
      />
      <ParamTable
        title="Query Parameters"
        params={[
          { field: 'amount', type: 'number', required: true, description: 'Amount to convert' },
          { field: 'from', type: 'string', required: true, description: 'Source currency code (ISO 4217)' },
          { field: 'to', type: 'string', required: true, description: 'Target currency code (ISO 4217)' },
        ]}
      />
      <p className="font-mono text-[10px] text-bone-white/30 mt-3 mb-1">
        /api/currency/convert?amount=100&from=EUR&to=USD
      </p>
      <CodeBlock
        language="json"
        code={`{
  "original_amount": 100,
  "converted_amount": 108.45,
  "from": "EUR",
  "to": "USD",
  "rate": 1.0845,
  "bid": 1.0843,
  "ask": 1.0847,
  "midpoint": 1.0845,
  "timestamp": "2026-03-18T12:00:00.000Z"
}`}
      />

      <EndpointCard
        id="spot"
        method="GET"
        path="/api/currency/spot"
        description="Real-time spot rates for one or more currency pairs."
      />
      <ParamTable
        title="Query Parameters"
        params={[
          { field: 'base', type: 'string', required: true, description: 'Base currency code' },
          { field: 'quotes', type: 'string', required: true, description: 'Comma-separated quote currency codes' },
          { field: 'data_set', type: 'string', required: false, description: 'Data source filter (OANDA, CRYPTO, etc.)' },
        ]}
      />
      <CodeBlock
        language="json"
        code={`{
  "base": "USD",
  "quotes": [
    { "currency": "EUR", "bid": 0.9218, "ask": 0.9222, "midpoint": 0.9220 },
    { "currency": "GBP", "bid": 0.7891, "ask": 0.7895, "midpoint": 0.7893 },
    { "currency": "JPY", "bid": 149.12, "ask": 149.18, "midpoint": 149.15 }
  ],
  "timestamp": "2026-03-18T12:00:00.000Z",
  "data_set": "OANDA"
}`}
      />

      <EndpointCard
        id="currencies"
        method="GET"
        path="/api/currency/currencies"
        description="List all available currencies for a given dataset."
      />
      <ParamTable
        title="Query Parameters"
        params={[
          { field: 'data_set', type: 'string', required: false, description: 'Filter by data source (e.g. OANDA, CRYPTO, EUCB)' },
        ]}
      />
      <CodeBlock
        language="json"
        code={`{
  "currencies": [
    { "code": "USD", "name": "US Dollar" },
    { "code": "EUR", "name": "Euro" }
  ],
  "count": 180,
  "data_set": "OANDA"
}`}
      />

      <EndpointCard
        id="candle"
        method="GET"
        path="/api/currency/candle"
        description="Single daily OHLC candle for a currency pair."
      />
      <ParamTable
        title="Query Parameters"
        params={[
          { field: 'base', type: 'string', required: true, description: 'Base currency code' },
          { field: 'quotes', type: 'string', required: true, description: 'Quote currency code(s)' },
        ]}
      />
      <CodeBlock
        language="json"
        code={`{
  "base": "USD",
  "quotes": [{
    "currency": "EUR",
    "start_time": "2026-03-18T00:00:00Z",
    "open": { "bid": 0.9215, "ask": 0.9219, "midpoint": 0.9217 },
    "close": { "bid": 0.9220, "ask": 0.9224, "midpoint": 0.9222 },
    "average": { "bid": 0.9218, "ask": 0.9222, "midpoint": 0.9220 },
    "high": { "bid": 0.9230, "ask": 0.9234, "midpoint": 0.9232 },
    "low": { "bid": 0.9210, "ask": 0.9214, "midpoint": 0.9212 }
  }],
  "timestamp": "2026-03-18T12:00:00.000Z"
}`}
      />
    </>
  );
}
